import { useMemo } from 'react';
import type React from 'react';

import { getDomain } from 'tldts';

import { Redirect } from '@change-corgi/core-react/router';
import { TrackingContext } from '@change-corgi/core-react/tracking';
import type { TrackingProperties } from '@change-corgi/tracking-properties';

import { useQueryParamValue } from '#src/app/shared/hooks/location';

import { Layout } from '../layout';

export const ExternalUrl: React.FC<EmptyProps> = () => {
	const url = useQueryParamValue('to');
	const source = useQueryParamValue('source');

	const validUrl = useMemo(() => {
		try {
			return !!url && !!new URL(url);
		} catch (e) {
			return false;
		}
	}, [url]);

	// Registrable domain without subdomain (e.g. `bbc.co.uk`); null for IPs, localhost, etc.
	const urlDomain = useMemo(() => (url ? getDomain(url) : null), [url]);

	// `source` is `<source>:<sourceId>` — split on the first
	// `:` only, since a sourceId (e.g. a vanity slug) could itself contain one. Absent or malformed
	// (no `:`, or not even a string — `useQueryParamValue`'s cast doesn't rule out qs parsing a
	// nested query param like `source[key]=value` into an object) values are tolerated: this is
	// tracking metadata, never a reason to block the redirect.
	const trackingProperties: TrackingProperties = useMemo(() => {
		const base = { url, ...(urlDomain ? { url_domain: urlDomain } : {}) };
		if (typeof source !== 'string') return base;
		const separatorIndex = source.indexOf(':');
		if (separatorIndex === -1) return { ...base, source };
		return { ...base, source: source.slice(0, separatorIndex), source_id: source.slice(separatorIndex + 1) };
	}, [url, urlDomain, source]);

	if (!url) {
		return <Redirect to="/" reason="missing_url" />;
	}
	if (!validUrl) {
		return <Redirect to="/" reason="invalid_url" />;
	}

	return (
		<TrackingContext properties={trackingProperties}>
			<Layout />
		</TrackingContext>
	);
};
